import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth, initialsOfUser } from '../auth.jsx'
import { useRbac } from '../lib/rbac.js'
import { useTheme } from '../lib/theme.jsx'
// The run host's live work, for the chip below: a background run or a
// streaming answer must have UI on EVERY page — "running with no UI" is the
// confirmed bug (two lists, two campaign drafts, same 50 leads) that runHost's
// construction exists to prevent, and this chip is the UI half of it.
import { getRun, getStream, subscribeRun, subscribeStream } from '../lib/runHost.js'
import { currentScope } from '../lib/userScope.js'
import { MoonIcon, SunIcon, ChevronDownIcon, SparklesIcon } from './icons.jsx'
import NotificationsMenu from './NotificationsMenu.jsx'
import GlobalSearch from './GlobalSearch.jsx'

/**
 * The honest step count for the chip: which step is open, out of how many.
 * Read the same way Chat.jsx's progress line reads the graph — the first node
 * actually marked running names the step; several open at once still name the
 * first (the count stays true: N/M is a position, not a claim that the rest
 * are idle); none open yet means the pool is still loading, and that is said
 * rather than pretending a step has begun.
 */
function runStepLabel(run) {
  const passes = Array.isArray(run?.passes) ? run.passes : []
  const total = passes.length
  if (!total) return 'starting'
  const live = passes.findIndex((p) => p?.status === 'running')
  if (live >= 0) return `step ${live + 1}/${total}`
  const settled = passes.filter((p) => p?.status !== 'pending').length
  return settled ? `${settled}/${total} steps done` : 'loading leads'
}

/**
 * The background-work chip. Visible on ANY page whenever the host has a live
 * run or stream AND the reader is not already looking at that conversation;
 * clicking it opens /chat/<convoId>, where the live progress (or the landed
 * receipt) is. It disappears the moment the work lands — no toast, no stack.
 *
 * MEMBER-VISIBILITY (the standing rep rule): the chip may name the operation
 * and the chat — chat is every role's surface — and must never name a gated
 * page. It renders the run's own intent label and links only to /chat/…,
 * so there is nothing here a rep is not allowed to see.
 *
 * Exported: it also floats over the wizard routes (WizardLayout), which have
 * no Topbar — a run live while someone stages a campaign draft must have UI
 * there too, or "running with no UI" recurs on exactly the pages where a
 * second start would hurt most.
 */
export function LiveWorkChip() {
  // Re-read INSIDE the subscribing effect: a status change landing between
  // this component's render and the effect (the run's final announce firing
  // while a page mounts) is otherwise missed, and a finished host announces
  // nothing further — the chip would spin forever over finished work.
  const [run, setRun] = useState(() => getRun())
  const [stream, setStream] = useState(() => getStream())
  useEffect(() => {
    setRun(getRun())
    return subscribeRun(() => setRun(getRun()))
  }, [])
  useEffect(() => {
    setStream(getStream())
    return subscribeStream(() => setStream(getStream()))
  }, [])
  const navigate = useNavigate()
  const location = useLocation()

  // Only the SIGNED-IN account's work. After a SPA sign-out/sign-in mid-run
  // the host may still be finishing the previous account's operation — naming
  // it here would leak one user's work to the next, and the /chat/<id> link
  // would only bounce (the thread is not theirs). The Chat page carries the
  // neutral "background work from a previous sign-in" line instead.
  const scope = currentScope()
  const ours = (w) => (w.scope || 'anon') === (scope || 'anon')
  const liveRun = run && run.status === 'running' && ours(run) ? run : null
  const liveStream = stream && stream.status === 'streaming' && ours(stream) ? stream : null
  // A run outranks a stream when both are live: it is the heavier work, and
  // one chip is the point — subtle, not a toast storm.
  const work = liveRun || liveStream
  if (!work || !work.convoId) return null
  // The conversation the work reports into is already on screen — the thread
  // itself is the better UI, so the chip stands down.
  if (location.pathname === `/chat/${work.convoId}`) return null

  const label = liveRun ? `${liveRun.operationLabel} · ${runStepLabel(liveRun)}` : 'Answer writing…'
  return (
    <button
      type="button"
      onClick={() => navigate(`/chat/${work.convoId}`)}
      title={
        liveRun
          ? 'An operation is running in the background — open its chat'
          : 'An answer is writing in the background — open its chat'
      }
      className="mr-1 inline-flex min-w-0 max-w-[280px] cursor-pointer items-center gap-x-2 rounded-full bg-primary-tint px-3 py-[6px] text-[12px] font-semibold text-primary-dark transition-colors hover:bg-panel"
    >
      <span
        aria-hidden="true"
        className="h-[11px] w-[11px] flex-none animate-spin rounded-full [border:1.5px_solid_currentColor] [border-top-color:transparent] motion-reduce:animate-none"
      />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  )
}

// `assistantOpen`/`onToggleAssistant` wire the AI-assistant trigger (next to
// the notifications bell) to the drawer owned by AppLayout.
export default function Topbar({ assistantOpen, onToggleAssistant, onOpenNav }) {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  const name = user?.name || 'User'
  // The role line is cockpit furniture: members see just their name — no
  // "Sales Rep" badge quietly ranking them every time they look up.
  const { roleLabel, can } = useRbac()
  const role = can('pages.workspace') ? roleLabel : ''
  const email = user?.email || ''

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return
    function onPointer(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <header className="sticky top-0 z-20 flex items-center justify-end gap-x-2 [border-bottom:1px_solid_var(--border)] bg-[rgba(255,255,255,0.85)] px-5 py-1.5 backdrop-blur-[6px] dark:bg-[rgba(21,21,21,0.85)] max-md:px-3">
      {/* Phone only: opens the nav drawer. 44px square — a thumb target, not a
          pointer target, because this is used one-handed while driving between
          calls. */}
      {onOpenNav && (
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="Open menu"
          className="mr-auto hidden h-[40px] w-[40px] cursor-pointer place-items-center rounded-[9px] border-none bg-transparent text-dim transition-colors hover:bg-panel hover:text-ink max-md:grid"
        >
          <svg viewBox="0 0 24 24" className="h-[20px] w-[20px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {/* coolicons: line/Hamburger_MD.svg */}
            <path d="M5 17H19M5 12H19M5 7H19" />
          </svg>
        </button>
      )}
      <GlobalSearch />

      {/* Background work, if any — see LiveWorkChip above. Sits beside the
          search so it is visible on every page without shouting. */}
      <LiveWorkChip />

      <div className="mr-1.5 flex items-center gap-x-1.5">
        <button
          className="relative grid h-[34px] w-[34px] place-items-center rounded-[9px] bg-transparent text-dim transition-colors hover:bg-panel hover:text-ink"
          type="button"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
        >
          {theme === 'dark' ? <SunIcon className="h-[18px] w-[18px]" /> : <MoonIcon className="h-[18px] w-[18px]" />}
        </button>
        <button
          className={`relative grid h-[34px] w-[34px] place-items-center rounded-[9px] transition-colors ${
            assistantOpen
              ? 'bg-primary-tint text-primary-dark'
              : 'bg-transparent text-dim hover:bg-panel hover:text-ink'
          }`}
          type="button"
          data-assistant-launcher
          aria-label={assistantOpen ? 'Close assistant' : 'Ask the assistant'}
          aria-expanded={assistantOpen}
          title="Ask AI"
          onClick={onToggleAssistant}
        >
          <SparklesIcon className="h-[18px] w-[18px]" />
        </button>
        <NotificationsMenu />
      </div>

      <div className="relative" ref={menuRef}>
        <button
          className="flex items-center gap-x-2.5 rounded-[10px] bg-transparent py-[5px] pl-[5px] pr-2 transition-colors hover:bg-panel"
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="grid h-[32px] w-[32px] flex-none place-items-center rounded-[10px] bg-[#18181b] text-[12px] font-semibold tracking-[0.02em] text-white">
            {initialsOfUser(user)}
          </span>
          {/* min-w-0 + truncate: a long self-chosen name must not push the header
              around — it clips with an ellipsis and keeps the full text in the title. */}
          <span className="flex min-w-0 flex-col items-start leading-[1.2]">
            {/* No `capitalize` here: the name is the user's own text now, and
                text-transform would rewrite "ludwig van beethoven" as "Ludwig
                Van Beethoven". The email-derived fallback is already tidied in
                displayName(). */}
            <span
              className="max-w-[168px] truncate text-[14px] font-semibold text-ink"
              title={name}
            >
              {name}
            </span>
            {role && <span className="max-w-[168px] truncate text-[12px] text-dim">{role}</span>}
          </span>
          <ChevronDownIcon
            className={`h-4 w-4 text-faint transition-transform${open ? ' rotate-180' : ''}`}
          />
        </button>

        {/* max-w as well as min-w: a 50-character name part has nowhere to wrap,
            so without a cap this menu grows past 490px and — being right-anchored
            — its left edge lands off-screen on a phone. */}
        {open && (
          <div
            className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[244px] max-w-[min(320px,calc(100vw-24px))] origin-top-right animate-[menuReveal_0.16s_ease] rounded-[12px] border border-line bg-surface p-1.5 shadow-[0_12px_32px_rgba(15,23,42,0.16)]"
            role="menu"
          >
            <div className="px-3 pt-2.5 pb-3">
              <div className="break-words text-[15px] font-bold text-ink">{name}</div>
              {role && <div className="mt-0.5 text-[13px] text-dim">{role}</div>}
              {email && <div className="mt-0.5 break-all text-[13px] text-faint">{email}</div>}
            </div>
            <div className="my-1 h-px bg-line" />
            <button
              className="block w-full rounded-[8px] bg-transparent px-3 py-[9px] text-left text-[14px] font-medium text-ink transition-colors hover:bg-panel"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                navigate('/settings')
              }}
            >
              Settings
            </button>
            <button
              className="block w-full rounded-[8px] bg-transparent px-3 py-[9px] text-left text-[14px] font-medium text-danger transition-colors hover:bg-[#fef2f2]"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                logout()
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
